import React, { useState } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import SectionHeader from './components/SectionHeader';
import CoffeeCard from './components/CoffeeCard';
import OriginMap from './components/OriginMap';
import RoastFinder from './components/RoastFinder';
import Subscriptions from './components/Subscriptions';
import Equipment from './components/Equipment';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import Toast from './components/Toast';
import { coffees } from './data';

function App() {
  // Global State
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [toast, setToast] = useState(null);

  // Cart Functions
  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { ...item, qty: 1 }];
    });
    
    // Show Toast
    setToast({ message: `${item.name || item.title} added to cart.` });
    setIsCartOpen(true); // Auto open cart on add for better UX
    setTimeout(() => setToast(null), 3000);
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const updateQuantity = (id, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.qty + delta);
        return { ...item, qty: newQty };
      }
      return item;
    }));
  };

  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  return (
    <div className="min-h-screen bg-cream font-sans selection:bg-moss selection:text-cream relative">
      <Navbar cartCount={cartCount} onOpenCart={() => setIsCartOpen(true)} />
      
      <main id="main-content">
        <Hero />
        
        {/* Featured Coffees Section */}
        <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12" id="shop-coffee">
          <SectionHeader 
            title="Fresh Roasts" 
            subtitle="The latest arrivals from our roasting log."
            icon="Coffee"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
            {coffees.map(coffee => (
              <CoffeeCard key={coffee.id} coffee={coffee} onAddToCart={addToCart} />
            ))}
          </div>
          <div className="text-center mt-24">
            <button className="group relative inline-flex items-center gap-3 px-12 py-5 border-2 border-espresso text-espresso font-bold uppercase tracking-[0.2em] text-[11px] overflow-hidden transition-all duration-300 hover:text-cream cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-moss/50">
              <span className="relative z-10">Explore All Origins</span>
              <div className="absolute inset-0 bg-espresso translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
            </button>
          </div>
        </section>

        <OriginMap />
        
        <RoastFinder onAddToCart={addToCart} />
        
        <Subscriptions onAddToCart={addToCart} />
        
        <Equipment onAddToCart={addToCart} />
        
        {/* Trust Section */}
        <section className="py-20 bg-espresso text-cream overflow-hidden border-y border-white/5" aria-hidden="true">
          <div className="flex whitespace-nowrap animate-marquee">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-12 px-6">
                <span className="font-serif text-3xl font-bold italic opacity-30">Freshly Roasted</span>
                <span className="h-2 w-2 bg-moss rounded-full"></span>
                <span className="font-serif text-3xl font-bold italic opacity-30">Direct Trade</span>
                <span className="h-2 w-2 bg-moss rounded-full"></span>
                <span className="font-serif text-3xl font-bold italic opacity-30">Whole Bean</span>
                <span className="h-2 w-2 bg-moss rounded-full"></span>
                <span className="font-serif text-3xl font-bold italic opacity-30">Climate Neutral</span>
                <span className="h-2 w-2 bg-moss rounded-full"></span>
              </div>
            ))}
          </div>
        </section>

        <FAQ />
      </main>

      <Footer />

      {/* Overlays & Notifications */}
      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
        cart={cart}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
      />
      
      <Toast toast={toast} onClose={() => setToast(null)} />

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          display: flex;
          width: fit-content;
          animation: marquee 30s linear infinite;
        }
      `}} />
    </div>
  );
}

export default App;